import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import './UploadQuestions.css';
import { useAuth } from '../../utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';

const UploadQuestions = () => {
    const { auth } = useAuth();
    const [questions, setQuestions] = useState([]);
    const [file, setFile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const navigate = useNavigate();

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleFileUpload = () => {
        if (!file) {
            alert("Please select a file first");
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            processQuestions(json);
        };
        reader.readAsArrayBuffer(file);
    };

    const convertToString = (value) => {
        if (value === undefined || value === null) {
            return '';
        }
        return String(value).trim();
    };

    const processQuestions = (data) => {
        const headers = data[0];
        const uniqueQuestions = new Set();
        const questionsData = data.slice(1).map((row) => {
            let question = {};
            headers.forEach((header, index) => {
                question[header] = convertToString(row[index]);
            });

            const correctAnswer = question['Correct Answer'];
            question.options = [
                { OptionText: question['Option 1'], IsCorrect: correctAnswer === 'A' },
                { OptionText: question['Option 2'], IsCorrect: correctAnswer === 'B' },
                { OptionText: question['Option 3'], IsCorrect: correctAnswer === 'C' },
                { OptionText: question['Option 4'], IsCorrect: correctAnswer === 'D' }
            ];

            // Validate the options and correct answer
            if (!correctAnswer || !['A', 'B', 'C', 'D'].includes(correctAnswer)) {
                question.options.forEach(option => option.IsCorrect = false);
            }

            // Check for duplicate questions
            if (uniqueQuestions.has(question['Question'])) {
                return null; // Skip duplicate questions
            }
            uniqueQuestions.add(question['Question']);

            return {
                QuestionText: question['Question'],
                Options: question.options
            };
        }).filter(question => question !== null);

        setQuestions(questionsData);
    };

    const handleEdit = (index) => {
        setCurrentQuestion({ ...questions[index], index });
        setIsEditing(true);
    };

    const handleDelete = (index) => {
        const newQuestions = questions.filter((_, i) => i !== index);
        setQuestions(newQuestions);
    };

    const handleSaveEdit = () => {
        const newQuestions = [...questions];
        newQuestions[currentQuestion.index] = { 
            QuestionText: currentQuestion.QuestionText, 
            Options: currentQuestion.Options 
        };
        setQuestions(newQuestions);
        setIsEditing(false);
        setCurrentQuestion(null);
    };

    const handleSubmit = async () => {
        try {
            const payload = questions.map((question, index) => ({
                questionId: index + 1, // Assuming questionId needs to be unique
                questionText: question.QuestionText,
                categoryIds: [], // Assuming no categoryIds for now
                options: question.Options.map((option, optionIndex) => ({
                    optionId: optionIndex + 1, // Assuming optionId needs to be unique within the question
                    optionText: option.OptionText,
                    isCorrect: option.IsCorrect,
                })),
            }));

            console.log('Payload:', JSON.stringify(payload, null, 2));

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Questions/import`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${auth.token}`
                }
            });

            if (response.status === 200) {
                alert('Questions imported successfully!');
                // Clear the screen or redirect to admin dashboard
                setQuestions([]);
                navigate('/admin');
            } else {
                alert('Failed to import questions.');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('An error occurred while importing questions.');
        }
    };

    const handleOptionChange = (index, value) => {
        const updatedOptions = [...currentQuestion.Options];
        updatedOptions[index].OptionText = value;
        setCurrentQuestion({ ...currentQuestion, Options: updatedOptions });
    };

    const handleCorrectAnswerChange = (index) => {
        const updatedOptions = currentQuestion.Options.map((option, idx) => ({
            ...option,
            IsCorrect: idx === index
        }));
        setCurrentQuestion({ ...currentQuestion, Options: updatedOptions });
    };

    return (
        <div className="container">
            <h2 className="header">Upload Questions</h2>
            <input type="file" onChange={handleFileChange} />
            <button onClick={handleFileUpload} className="upload-button">Upload</button>
            {questions.length > 0 && (
                <div className="card-container">
                    <h3 className="header">Verify Questions</h3>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Question</th>
                                <th>Options</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.map((question, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{question.QuestionText}</td>
                                    <td>
                                        <ul>
                                            {question.Options.map((option, idx) => (
                                                <li key={idx} className="option-box">
                                                    {option.OptionText} {option.IsCorrect && <span className="correct-answer"><FaCheck /> (Correct)</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button onClick={() => handleEdit(index)} className="edit-button">Edit</button>
                                            <button onClick={() => handleDelete(index)} className="delete-button">Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={handleSubmit} className="submit-button">Submit</button>
                </div>
            )}
            {isEditing && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Question</h3>
                        <label>
                            Question Text:
                            <input
                                type="text"
                                value={currentQuestion.QuestionText}
                                onChange={(e) => setCurrentQuestion({ ...currentQuestion, QuestionText: e.target.value })}
                            />
                        </label>
                        <div>
                            {currentQuestion.Options.map((option, index) => (
                                <div key={index}>
                                    <label>
                                        Option {index + 1}:
                                        <input
                                            type="text"
                                            value={option.OptionText}
                                            onChange={(e) => handleOptionChange(index, e.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="correctAnswer"
                                            checked={option.IsCorrect}
                                            onChange={() => handleCorrectAnswerChange(index)}
                                        />
                                        Correct
                                    </label>
                                </div>
                            ))}
                        </div>
                        <button onClick={handleSaveEdit} className="save-button">Save</button>
                        <button onClick={() => setIsEditing(false)} className="cancel-button">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UploadQuestions;
